import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreModule } from '@ngrx/store';
import { SecurityModule } from './modules/security/security.module';
import { JoyrideModule } from 'ngx-joyride';
import { AuthorizationModule } from './modules/authorization/authorization.module';
import { TraceService } from '@sentry/angular';
import { Router } from '@angular/router';

import * as WebFont from 'webfontloader';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { LocationStrategy } from '@angular/common';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { GlobalDataStorageService } from '@dash/randy/security/service/global-data-storage.service';
import { COLLECTION_LOCALE } from '@dash/randy/tokens/collection-item-locale.token';
import { SELECTED_APPLICATION_ID } from '@dash/randy/tokens/selected-application-id.token';
import { MatIconRegistry } from '@angular/material/icon';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport,
} from '@angular/common/http';
import { RequestObservabilityInterceptor } from '@dash/randy/shared/observability/request-observability.interceptor';
import { RandyErrorHandler } from '@dash/randy/shared/errors/randy-error-handler';
import { provideRandyDates } from '@dash/randy/shared/dates/randy-date-adapter';

WebFont.load({
  custom: { families: ['Material Icons', 'Material Icons Outline'] },
});

export const getCollectionLocale = (tokenStorageService: GlobalDataStorageService) => {
  return tokenStorageService.getCollectionLocale();
};

export const getSelectedApplicationId = (tokenStorageService: GlobalDataStorageService) => {
  return () => tokenStorageService.getSelectedApplication()?.id ?? null;
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({
      maxAge: 150, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
      connectInZone: true,
    }),
    SecurityModule.forRoot(),
    AuthorizationModule,
    JoyrideModule.forRoot(),
    NgxSkeletonLoaderModule.forRoot(
      {
        animation: 'pulse',
        loadingText: 'Laden...',
        theme: {
          extendsFromRoot: true,
          'background-color': 'var(--mat-sys-inverse-on-surface)',
        },

      }),
  ],
  providers: [
    provideRandyDates(),
    provideHttpClient(withJsonpSupport(), withInterceptorsFromDi()),
    {
      provide: ErrorHandler,
      useClass: RandyErrorHandler,
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
        appearance: 'fill',
      },
    },
    {
      provide: COLLECTION_LOCALE,
      useFactory: getCollectionLocale,
      deps: [GlobalDataStorageService],
    },
    {
      provide: SELECTED_APPLICATION_ID,
      useFactory: getSelectedApplicationId,
      deps: [GlobalDataStorageService],
    },
    provideAppInitializer(async () => {
      inject(TraceService); // ensures it's instantiated
      return () => {
      };
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestObservabilityInterceptor,
      multi: true,
    },
    { provide: LocationStrategy, useClass: environment.locationStrategy },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private matIconRegistry: MatIconRegistry, private domSanitizer: DomSanitizer,
  ) {
    try {
      matIconRegistry.addSvgIcon(
        'columns_edit',
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/columns_edit.svg'),
      );
    } catch (e) {
      console.error(e, 'Couldnt load columns svg icon');
    }
  }
}
